import {HttpErrorResponse} from '@angular/common/http';
import {Component, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';

import {TranslateService} from '@ngx-translate/core';

import {EMPTY, Observable, of} from 'rxjs';
import {catchError, filter, finalize, map, switchMap, takeUntil, tap} from 'rxjs/operators';

import isNull from 'lodash-es/isNull';
import isNumber from 'lodash-es/isNumber';

import * as moment from 'moment';

import {ToastrService} from 'ngx-toastr';
import {isNumberType} from '../../../../+reports/preview';
import {ContractIndexCriteriaDTO} from '../../../../shared/api/GaApi';
import {EmployeesUtilsService} from '../../../../shared/employees/employees-utils.service';
import {EmployeesContextUtilsService} from '../../../../shared/employees/utils/employee-context-utils.service';
import {FieldType} from '../../../../shared/form-generator/fields/fields.interfaces';
import {Validators as GpValidators} from '../../../../shared/forms';
import {LocalizationService} from '../../../../shared/localization/localization.service';
import {GPTranslateService} from '../../../../shared/localization/translate.service';
import {ModalService} from '../../../../shared/modal';
import {
  CriterionDescriptor,
  Entity
} from '../../../../shared/population/create-population/create-population.interfaces';
import {CreatePopulationService} from '../../../../shared/population/create-population/create-population.service';
import {DefaultPagination, Pagination} from '../../../../shared/ui';
import {SelectItem} from '../../../../shared/ui/select/select-item';
import {EmpPopulationCriterion, ListItem} from '../../user-rights.interfaces';
import {UserRightsService} from '../../user-rights.service';
import {ManageAccessCriterionModalComponent} from '../manage-access-criterion-modal.component';
import {AccessTypes, CRITERION_PER_PAGE, STATE} from '../manage-access-criterion-modal.interface';
import {
  getEntityLazyValue,
  isDateType,
  isEntityType,
  ManageAccessCriterionModalService
} from '../manage-access-criterion-modal.service';

@Component({
  selector: 'gp-manage-population-access-criterion-modal',
  templateUrl: './manage-population-access-criterion-modal.component.html',
  styleUrls: ['./manage-population-access-criterion-modal.component.scss'],
})
export class ManagePopulationAccessCriterionModalComponent extends ManageAccessCriterionModalComponent implements OnInit, OnDestroy {
  public criterionZoneDescriptors: CriterionDescriptor[] = [];
  public managePopulationAccessCriterionForm: UntypedFormGroup;
  public criterionForm: UntypedFormGroup;
  public contractIndexCriteriaZones: ListItem[] = [];
  public zoneTypes: ListItem[] = [];
  public accessModes: ListItem[] = [];
  public contractIndexCriterias: ContractIndexCriteriaDTO[] = [];
  public currentState: STATE | '' = STATE.INITIAL_STATE;
  public selectedZone: SelectItem = null;
  public zoneData: EmpPopulationCriterion[] = [];
  public pagination: Pagination = new DefaultPagination(CRITERION_PER_PAGE);
  public selectedZoneName = null;
  public selectedZoneType = null;
  public selectedZoneGroup = null;
  public selectedZoneMetadata = null;
  private selectedZoneTransId = null;
  public MAX_ALLOWED_LENGTH = 75;
  public lazyValue: string;
  public entityList: Entity[] = [];
  public entityList$: Observable<Entity[]>;
  public booleanValueChecked = false;

  constructor(
    private modalService: ModalService,
    private toastService: ToastrService,
    private translate: TranslateService,
    private formBuilder: UntypedFormBuilder,
    private userRightService: UserRightsService,
    private employeesContextUtilsService: EmployeesContextUtilsService,
    private employeesUtilsService: EmployeesUtilsService,
    private translateService: TranslateService,
    private createPopulationService: CreatePopulationService,
    private gpTranslateService: GPTranslateService,
    private localizationService: LocalizationService,
    private manageAccessCriterionModalService: ManageAccessCriterionModalService
  ) {
    super();
    this.criterionForm = this.formBuilder.group({
      accessMode: ['', Validators.required],
      value: ['', [Validators.required]]
    });

    this.managePopulationAccessCriterionForm = this.formBuilder.group({
      contractIndexCriteriaZoneName: ['', Validators.required]
    });
  }

  public get isInitialState(): boolean {
    return !this.selectedZone && this.currentState === STATE.INITIAL_STATE;
  }

  public get isCriterionNotFound(): boolean {
    return this.selectedZone && this.isContentLoaded && !this.isAddCriterionActive && !this.zoneData?.length;
  }

  public ngOnInit(): void {
    this.getContractIndexCriteria();
    this.getAndPopulateContractIndexCriteriaZones();

    this.accessModes = Object.values(AccessTypes).map((type: string) => ({
      id: type,
      text: type
    }));

    this.criterionForm.get('value').valueChanges
      .pipe(
        filter(() => isEntityType(this.selectedZoneType)),
        tap((value) => this.lazyValue = getEntityLazyValue(value, this.entityList)),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalService.modalClosed.emit('manage-population-access-criterion');
  }

  public showAddCriterionFormFields(): void {
    this.hideAddOrUpdateForm(this.criterionForm);

    if (this.selectedZoneType === FieldType.BOOLEAN) {
      this.criterionForm.patchValue({
        value: this.booleanValueChecked
      });
    }

    this.currentState = STATE.CREATE_NEW_CRITERION;
  }

  public selectZone(data: SelectItem): void {
    this.selectedZone = {...data} as SelectItem;
    this.selectedZoneName = this.selectedZone?.id;
    this.handleZoneType();

    this.pagination.currentPage.number = 0;
    isEntityType(this.selectedZoneType) ? this.pageChanged() : this.refreshTableData();
  }

  public pageChanged(): void {
    this.entityList$.pipe(
      tap(entityList => this.entityList = entityList),
      switchMap(() => this.fetchEmpPopulationCriterion()),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  public removeSelectZone(): void {
    this.selectedZone = null;
    this.currentState = STATE.INITIAL_STATE;
    this.criterionForm.reset();
  }

  public createPopulationAccessCriterion(): void {
    this.currentState = STATE.CREATE_NEW_CRITERION_IN_PROGRESS;
    this.addOrUpdatePopulationAccessCriterion();
  }

  public updatePopulationAccessCriterion(): void {
    this.currentState = STATE.UPDATE_CRITERION_IN_PROGRESS;
    this.addOrUpdatePopulationAccessCriterion();
  }

  public zoneSelected(zone): void {
    this.selectedZoneTransId = zone.text;
  }

  public handleDeleteCriterionConfirmation(): void {
    this.currentState = STATE.DELETE_CRITERION_IN_PROGRESS;

    this.userRightService
      .removePopulationAccessCriterion(this.criterionToBeDeleted)
      .pipe(
        tap(() => {
          this.employeesUtilsService.accessCriterionsChanged$.next();
          this.discardDeleteCriterion();
          this.toastService.success(
            this.translate.instant(`SETTINGS.user_rights.access_criterion_deleted_successfully`)
          );
        }),
        switchMap(() => this.fetchEmpPopulationCriterion()),
        catchError((errorResponse: HttpErrorResponse) => {
          const errorMessage = `${this.translate.instant(`SETTINGS.user_rights.access_criterion_deletion_failed`)}: ${
            errorResponse?.error?.message
          }`;
          this.toastService.error(errorMessage);
          return EMPTY;
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  public updateCriterion(zone: EmpPopulationCriterion): void {
    this.showUpdateCriterionFormFields(this.criterionForm);

    this.criterionToBeUpdated = zone?.id;
    this.criterionForm.patchValue({
      accessMode: zone.access,
      value: zone.value[zone?.zoneName]?.value
    });
  }

  public checkBooleanValue(zone: EmpPopulationCriterion): boolean {
    return JSON.parse(<string>zone.value[zone?.zoneName]?.value);
  }

  public getReadableName(zone: EmpPopulationCriterion): string  {
    if (zone?.readableName) {
      return zone.readableName;
    } else {
      if (zone.value[zone?.zoneName]?.type === FieldType.DATE) {
        return moment(zone.value[zone?.zoneName]?.value.toString()).format(this.localizationService.dateFormat);
      }
      return zone.value[zone?.zoneName]?.transId.toString();
    }
  }

  private handleZoneType(): void {
    const criteriaDescriptors: CriterionDescriptor = this.criterionZoneDescriptors
      .filter(descriptor => descriptor.zoneID === this.selectedZoneName)[0];

    this.selectedZoneType = criteriaDescriptors.type;
    this.selectedZoneGroup = criteriaDescriptors.zoneGroup;
    this.selectedZoneMetadata = null;
    this.selectedZoneTransId = null;
    const valueControl = this.criterionForm.get('value');

    switch (this.selectedZoneType) {
      case FieldType.LIST:
        this.selectedZoneMetadata = this.manageAccessCriterionModalService.createListTree(criteriaDescriptors.metadatas?.items);
        break;

      case FieldType.TREE:
        this.selectedZoneMetadata = this.manageAccessCriterionModalService.createTree(criteriaDescriptors.metadatas?.items);
        break;

      case FieldType.ENTITY:
      case FieldType.ENTITY_DATA:
      case FieldType.ENTITY_PREFIXED:
        this.selectedZoneMetadata = criteriaDescriptors.metadatas?.rel;
        this.entityList$ = this.createPopulationService.getEntityList(this.selectedZoneMetadata);
        break;

      case FieldType.DATE:
        break;

      case FieldType.BOOLEAN:
        break;

      default:
        if (valueControl) {
          const validators = [];
          validators.push(GpValidators.noWhitespaceValidator);
          validators.push(Validators.maxLength(this.MAX_ALLOWED_LENGTH));
          valueControl.setValidators(validators);
        }
        this.criterionForm.updateValueAndValidity();
        break;
    }
  }

  private fetchEmpPopulationCriterion(): Observable<{ data: EmpPopulationCriterion[], total: number }> {
    this.currentState = STATE.TABLE_LOADING;

    return this.userRightService
      .fetchEmpPopulationCriterionByZoneName(this.selectedZoneName, this.pagination.currentPage.number, this.pagination.perPage)
      .pipe(
        tap(response => {
          const rawData = response.data;
          if (isEntityType(this.selectedZoneType)) {
            rawData.map(zone => {
              const id = zone.value[zone?.zoneName]?.value;
              if (isNumber(id)) {
                zone.readableName = getEntityLazyValue(Number(id), this.entityList);
              }
            });
          }

          this.zoneData = rawData;
          this.pagination.update(response.total);
        }),
        catchError(() => of({data: [], total: 0})),
        finalize(() => {
          this.currentState = STATE.TABLE_LOADED;
        }),
        takeUntil(this.componentDestroyed$)
      );
  }

  private getAndPopulateContractIndexCriteriaZones() {
    const forAllowedCliRegsOnly = false;
    this.employeesContextUtilsService.getCriteriaDescriptors(forAllowedCliRegsOnly).pipe(
      map((criteriaDescriptors: CriterionDescriptor[]) => {
        if (criteriaDescriptors.length > 0) {
          this.criterionZoneDescriptors = criteriaDescriptors;
          this.criterionZoneDescriptors.forEach(criterionDescriptor => {
            criterionDescriptor.transIdsTranslations = this.getTransIdsTranslations(criterionDescriptor);
          });
        }
      }),
      tap(() => this.populateContractIndexCriteriaZones()),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  private refreshTableData(): void {
    this.fetchEmpPopulationCriterion().pipe(
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  private populateContractIndexCriteriaZones(): void {
    this.contractIndexCriteriaZones = this.criterionZoneDescriptors
      .map((descriptor: CriterionDescriptor) => ({
          id: descriptor.zoneID,
          text: this.gpTranslateService.getDisplayTranslation(descriptor.transIds),
          descriptionTransId: this.getTransIdsTranslations(descriptor)
        })
      );
  }

  private getContractIndexCriteria(): void {
    this.employeesContextUtilsService.getContractIndexCriteria().pipe(
      tap((response: ContractIndexCriteriaDTO[]) => {
        this.contractIndexCriterias = response;
      }),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  private addOrUpdatePopulationAccessCriterion(): void {
    let msg = '';
    const formData = this.criterionForm?.value;
    const selectedZoneName = this.managePopulationAccessCriterionForm.value.contractIndexCriteriaZoneName;

    const contractIndexCriteriaMappingDataIndex = this.contractIndexCriterias
      .filter(criteria =>
        criteria.name.toLowerCase() === selectedZoneName.toLowerCase())[0]?.dataIndice;

    const valueToNumeric = (isNumber(formData.value) ? formData.value : Number(formData.value));

    if (isDateType(this.selectedZoneType) || isNull(this.selectedZoneMetadata)) {
      this.selectedZoneTransId = formData.value;
    }

    const payload: EmpPopulationCriterion = {
      zoneName: selectedZoneName,
      access: formData.accessMode,
      value: {
        [selectedZoneName]: {
          type: this.selectedZoneType,
          value: (isNumberType(this.selectedZoneType) || isEntityType(this.selectedZoneType)) ?
            valueToNumeric : formData.value,
          transId: this.lazyValue ? this.lazyValue : this.selectedZoneTransId,
        },
      },
      groupZoneName: this.selectedZoneGroup,
      contractIndexCriteriaMapping: {
        data: contractIndexCriteriaMappingDataIndex?.toString(),
        value: formData.value
      }
    };

    const addOrUpdateCriterion$ = (this.isAddCriterionActive) ?
      this.userRightService.createPopulationAccessCriterion(payload) :
      this.userRightService.updatePopulationAccessCriterion(this.criterionToBeUpdated, payload);

    addOrUpdateCriterion$
      .pipe(
        tap(() => {
          this.employeesUtilsService.accessCriterionsChanged$.next();
          if (this.isAddCriterionActive) {
            this.showAddCriterionFormFields();
            msg = `SETTINGS.user_rights.access_criterion_added_successfully`;
          } else {
            this.showUpdateCriterionFormFields(this.criterionForm);
            msg = `SETTINGS.user_rights.access_criterion_modification_successfully`;
          }

          this.toastService.success(this.translate.instant(msg));
          this.refreshTableData();
        }),
        catchError((errorResponse: HttpErrorResponse) => {
          if (this.isAddCriterionActive) {
            this.currentState = STATE.CREATE_NEW_CRITERION;
            msg = `SETTINGS.user_rights.access_criterion_creation_failed`;
          } else {
            this.currentState = STATE.UPDATE_CRITERION;
            msg = `SETTINGS.user_rights.access_criterion_modification_failed`;
          }
          const errorMessage = `${this.translate.instant(msg)}: ${
            errorResponse?.error?.message
          }`;
          this.toastService.error(errorMessage);
          return EMPTY;
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  private getTransIdsTranslations(criterionDescriptor: CriterionDescriptor): string[] {
    const transIdsTranslations: string[] = [];
    for (const transId of criterionDescriptor.transIds) {
      const transIdTranslation = this.translateService.instant(transId);
      if (!transIdsTranslations.includes(transIdTranslation)) {
        transIdsTranslations.push(transIdTranslation);
      }
      if (transIdsTranslations.length === 4) {
        break;
      }
    }
    return transIdsTranslations;
  }
}
